/**
 * CloudSyncSheet - UI for cloud synchronization status and controls
 */

import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Cloud, CloudOff, RefreshCw, Upload, Download,
  CheckCircle2, AlertCircle, Wifi, WifiOff, User, Calendar
} from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import { useCloudSync } from '../../hooks/useCloudSync';
import { forceClearCalendarCache, getCalendarSyncStatus } from '../../services/userDataService';

export function CloudSyncSheet({ isOpen, onClose }) {
  const {
    isSyncing,
    syncError,
    lastSyncTime,
    isOnline,
    isAuthenticated,
    fullSync,
    pushToCloud,
    pullFromCloud,
    clearError
  } = useCloudSync();

  const [actionStatus, setActionStatus] = useState(null);

  // Statut du cache Google Calendar pour aujourd'hui
  const todayStr = new Date().toISOString().split('T')[0];
  const calendarStatus = getCalendarSyncStatus(todayStr);

  const handleClearCalendarCache = useCallback(() => {
    forceClearCalendarCache();
    setActionStatus({ type: 'success', message: 'Cache Calendar vidé. Le prochain chargement va resynchroniser.' });
  }, []);

  const handleFullSync = useCallback(async () => {
    setActionStatus(null);
    try {
      await fullSync();
      setActionStatus({ type: 'success', message: 'Synchronisation complète réussie !' });
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message });
    }
  }, [fullSync]);

  const handlePush = useCallback(async () => {
    setActionStatus(null);
    try {
      await pushToCloud();
      setActionStatus({ type: 'success', message: 'Données envoyées vers le cloud !' });
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message });
    }
  }, [pushToCloud]);

  const handlePull = useCallback(async () => {
    setActionStatus(null);
    try {
      await pullFromCloud();
      setActionStatus({ type: 'success', message: 'Données récupérées du cloud !' });
    } catch (err) {
      setActionStatus({ type: 'error', message: err.message });
    }
  }, [pullFromCloud]);

  const canSync = isOnline && isAuthenticated && !isSyncing;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Synchronisation Cloud"
      size="large"
    >
      <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">
        {/* Status indicators */}
        <div className="grid grid-cols-2 gap-3">
          {/* Online status */}
          <div className={`flex items-center gap-3 p-3 rounded-xl border ${
            isOnline
              ? 'bg-emerald-500/10 border-emerald-500/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            {isOnline ? (
              <Wifi className="w-5 h-5 text-emerald-400" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-400" />
            )}
            <div>
              <span className={`text-sm font-medium block ${isOnline ? 'text-emerald-300' : 'text-red-300'}`}>
                {isOnline ? 'En ligne' : 'Hors ligne'}
              </span>
              <span className="text-[10px] text-zinc-500">
                {isOnline ? 'Connexion active' : 'Pas de connexion'}
              </span>
            </div>
          </div>

          {/* Auth status */}
          <div className={`flex items-center gap-3 p-3 rounded-xl border ${
            isAuthenticated
              ? 'bg-cyan-500/10 border-cyan-500/30'
              : 'bg-zinc-800/30 border-zinc-700/50'
          }`}>
            {isAuthenticated ? (
              <User className="w-5 h-5 text-cyan-400" />
            ) : (
              <User className="w-5 h-5 text-zinc-500" />
            )}
            <div>
              <span className={`text-sm font-medium block ${isAuthenticated ? 'text-cyan-300' : 'text-zinc-400'}`}>
                {isAuthenticated ? 'Connecté' : 'Non connecté'}
              </span>
              <span className="text-[10px] text-zinc-500">
                {isAuthenticated ? 'Firebase Auth' : 'Connectez-vous'}
              </span>
            </div>
          </div>
        </div>

        {/* Last sync time */}
        {lastSyncTime && (
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span>
              Dernière sync: {lastSyncTime.toLocaleString('fr-FR')}
            </span>
          </div>
        )}

        {/* Status messages */}
        {(syncError || actionStatus) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 p-3 rounded-lg ${
              (actionStatus?.type || 'error') === 'success'
                ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`}
          >
            {(actionStatus?.type || 'error') === 'success' ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
            <span className="text-sm flex-1">
              {actionStatus?.message || syncError}
            </span>
            <button
              onClick={() => {
                setActionStatus(null);
                clearError();
              }}
              className="text-xs opacity-70 hover:opacity-100"
            >
              ✕
            </button>
          </motion.div>
        )}

        {/* Google Calendar status + reset */}
        <div className="space-y-2">
          <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
            Google Calendar
          </h3>
          <div className={`flex items-center gap-3 p-3 rounded-xl border ${
            calendarStatus.status === 'ok'
              ? 'bg-emerald-500/10 border-emerald-500/30'
              : calendarStatus.status === 'error'
                ? 'bg-red-500/10 border-red-500/30'
                : 'bg-zinc-800/30 border-zinc-700/50'
          }`}>
            <Calendar className={`w-4 h-4 flex-shrink-0 ${
              calendarStatus.status === 'ok' ? 'text-emerald-400'
              : calendarStatus.status === 'error' ? 'text-red-400'
              : 'text-zinc-500'
            }`} />
            <span className={`text-sm flex-1 ${
              calendarStatus.status === 'ok' ? 'text-emerald-300'
              : calendarStatus.status === 'error' ? 'text-red-300'
              : 'text-zinc-400'
            }`}>
              {calendarStatus.status === 'ok' && 'Synchronisé'}
              {calendarStatus.status === 'pending' && 'En attente de sync'}
              {calendarStatus.status === 'error' && (
                calendarStatus.errorCode === 404
                  ? 'Erreur : API Calendar non activée'
                  : calendarStatus.errorCode === 401 || calendarStatus.errorCode === 403
                    ? 'Erreur : authentification requise'
                    : calendarStatus.errorCode === 429
                      ? 'Limite de requêtes atteinte'
                      : 'Erreur de synchronisation'
              )}
            </span>
            <button
              onClick={handleClearCalendarCache}
              className="text-xs px-2 py-1 bg-zinc-700/50 hover:bg-zinc-600/50 text-zinc-300 rounded transition-all"
            >
              Réessayer
            </button>
          </div>
        </div>

        {/* Sync actions */}
        <div className="space-y-3">
          <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
            Actions de synchronisation
          </h3>

          {/* Full sync */}
          <button
            onClick={handleFullSync}
            disabled={!canSync}
            className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-cyan-950/30 to-purple-950/30 border border-cyan-500/30 hover:border-cyan-500/50 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              {isSyncing ? (
                <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
              ) : (
                <Cloud className="w-5 h-5 text-cyan-400" />
              )}
            </div>
            <div className="text-left flex-1">
              <span className="text-sm font-medium text-cyan-200 block">
                {isSyncing ? 'Synchronisation...' : 'Synchroniser tout'}
              </span>
              <span className="text-xs text-cyan-400/70">
                Fusionner les données locales et cloud
              </span>
            </div>
          </button>

          {/* Push to cloud */}
          <button
            onClick={handlePush}
            disabled={!canSync}
            className="w-full flex items-center gap-3 p-4 bg-zinc-800/30 border border-zinc-700/50 hover:border-zinc-600 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 bg-zinc-700/50 rounded-lg flex items-center justify-center">
              <Upload className="w-5 h-5 text-zinc-400" />
            </div>
            <div className="text-left flex-1">
              <span className="text-sm font-medium text-zinc-200 block">
                Envoyer vers le cloud
              </span>
              <span className="text-xs text-zinc-500">
                Sauvegarder toutes les données locales
              </span>
            </div>
          </button>

          {/* Pull from cloud */}
          <button
            onClick={handlePull}
            disabled={!canSync}
            className="w-full flex items-center gap-3 p-4 bg-zinc-800/30 border border-zinc-700/50 hover:border-zinc-600 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 bg-zinc-700/50 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5 text-zinc-400" />
            </div>
            <div className="text-left flex-1">
              <span className="text-sm font-medium text-zinc-200 block">
                Récupérer du cloud
              </span>
              <span className="text-xs text-zinc-500">
                Remplacer les données locales par le cloud
              </span>
            </div>
          </button>
        </div>

        {/* Not authenticated message */}
        {!isAuthenticated && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <CloudOff className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-sm text-amber-200 font-medium">
                  Connectez-vous pour synchroniser
                </p>
                <p className="text-xs text-amber-400/70 mt-1">
                  La synchronisation cloud nécessite une connexion Google.
                  Vos données sont stockées localement en attendant.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="text-center text-xs text-zinc-600 pt-2">
          <p>La synchronisation utilise Firebase Firestore.</p>
          <p>Les fichiers sont stockés dans Firebase Storage.</p>
        </div>
      </div>
    </BottomSheet>
  );
}

export default CloudSyncSheet;
